import Icon from '../../../components/AppIcon';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

async function sendSellOfferEmail(eventType, sellerEmail, offerData) {
  if (!sellerEmail) return;
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/send-sell-offer-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ eventType, sellerEmail, offerData }),
    });
  } catch (err) {
    console.error('Email notification error:', err);
  }
}

const STATUS_CONFIG = {
  'reserved': { label: 'Pending for Approval', bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  'pending_approval': { label: 'Pending for Approval', bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  'available': { label: 'Approved & Live', bg: '#d1fae5', color: '#065f46', dot: '#10b981' },
  'rejected': { label: 'Rejected', bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
  'paused': { label: 'Paused', bg: '#fef9c3', color: '#713f12', dot: '#ca8a04' },
  'withdrawn': { label: 'Withdrawn', bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' },
};

// Map a quotas row to the offer shape used in this component
const mapQuotaToOffer = (q) => ({
  id: q?.id,
  offerId: q?.offer_id || q?.id,
  readableOfferId: q?.readable_offer_id || q?.offer_id || q?.id,
  sellerName: q?.seller_name || '—',
  sellerMobile: q?.seller_mobile || '—',
  sellerEmail: q?.seller_email || '',
  factoryName: q?.factory_name || '—',
  grade: q?.grade || '—',
  quantity: parseFloat(q?.quantity) || 0,
  offeredRate: parseFloat(q?.price_per_ton) || 0,
  qualityRemarks: q?.quality_remarks || '—',
  paymentByDate: q?.available_from_date || '',
  liftingByDate: q?.offer_valid_until || '',
  season: q?.season || '—',
  status: q?.availability_status || 'reserved',
  isResumeRequest: q?.is_resume_request === true,
  originalPaymentByDate: q?.original_payment_by_date || '',
  originalLiftingByDate: q?.original_lifting_by_date || '',
  rejectionReason: q?.rejection_reason || '',
  createdAt: q?.created_at || '',
  photoUrls: Array.isArray(q?.photo_urls) ? q?.photo_urls : [],
  videoUrls: Array.isArray(q?.video_urls) ? q?.video_urls : [],
});

const isDateExtended = (origDate, newDate) => {
  if (!origDate || !newDate) return false;
  return new Date(newDate) > new Date(origDate);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d?.getTime())) return dateStr;
  return d?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || { label: status, bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' };
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: cfg?.bg, color: cfg?.color }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cfg?.dot }} />
      {cfg?.label}
    </span>
  );
};

// ─── View Details Modal ────────────────────────────────────────────────────────
const ViewDetailsModal = ({ offer, onClose }) => {
  const isResume = offer?.isResumeRequest;
  const paymentExtended = isResume && isDateExtended(offer?.originalPaymentByDate, offer?.paymentByDate);
  const liftingExtended = isResume && isDateExtended(offer?.originalLiftingByDate, offer?.liftingByDate);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="w-full max-w-2xl rounded-2xl shadow-2xl overflow-y-auto max-h-[90vh]" style={{ background: '#fffbf5', border: '1px solid #f3e8d0' }}>
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: '#f3e8d0' }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>Offer Details</h2>
            <p className="text-sm" style={{ color: '#6b7280' }}>{offer?.readableOfferId}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-amber-50 transition-colors">
            <Icon name="X" size={20} color="#6b7280" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Seller Name', value: offer?.sellerName },
              { label: 'Mobile', value: offer?.sellerMobile },
              { label: 'Factory Name', value: offer?.factoryName },
              { label: 'Grade', value: offer?.grade },
              { label: 'Quantity', value: `${offer?.quantity?.toLocaleString()} MT` },
              { label: 'Offered Rate', value: `₹${offer?.offeredRate?.toLocaleString()}/Ton` },
              { label: 'Season', value: offer?.season },
              { label: 'Quality Remarks', value: offer?.qualityRemarks },
              { label: 'Status', value: <StatusBadge status={offer?.status} /> },
            ]?.map(({ label, value }) => (
              <div key={label}>
                <p className="text-xs font-medium mb-0.5" style={{ color: '#9ca3af' }}>{label}</p>
                <p className="text-sm font-semibold" style={{ color: '#1f2937' }}>{value}</p>
              </div>
            ))}
          </div>

          {isResume ? (
            <div>
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1f2937' }}>
                <Icon name="GitCompare" size={16} color="#f59e0b" />
                Original vs New Values
              </h3>
              <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#f3e8d0' }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#fef3c7' }}>
                      <th className="px-4 py-2.5 text-left font-semibold" style={{ color: '#92400e' }}>Field</th>
                      <th className="px-4 py-2.5 text-left font-semibold" style={{ color: '#92400e' }}>Original</th>
                      <th className="px-4 py-2.5 text-left font-semibold" style={{ color: '#92400e' }}>New</th>
                      <th className="px-4 py-2.5 text-left font-semibold" style={{ color: '#92400e' }}>Changed?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: paymentExtended ? '#fef9ec' : '#fff' }}>
                      <td className="px-4 py-2.5 font-medium" style={{ color: '#374151' }}>Payment By</td>
                      <td className="px-4 py-2.5" style={{ color: '#6b7280' }}>{formatDate(offer?.originalPaymentByDate) || '—'}</td>
                      <td className="px-4 py-2.5 font-semibold" style={{ color: paymentExtended ? '#b45309' : '#374151' }}>{formatDate(offer?.paymentByDate)}</td>
                      <td className="px-4 py-2.5">
                        {paymentExtended
                          ? <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#b45309' }}><Icon name="ArrowUp" size={12} /> Extended</span>
                          : <span className="text-xs text-gray-400">No change</span>}
                      </td>
                    </tr>
                    <tr style={{ background: liftingExtended ? '#fef9ec' : '#f9fafb' }}>
                      <td className="px-4 py-2.5 font-medium" style={{ color: '#374151' }}>Lifting By</td>
                      <td className="px-4 py-2.5" style={{ color: '#6b7280' }}>{formatDate(offer?.originalLiftingByDate) || '—'}</td>
                      <td className="px-4 py-2.5 font-semibold" style={{ color: liftingExtended ? '#b45309' : '#374151' }}>{formatDate(offer?.liftingByDate)}</td>
                      <td className="px-4 py-2.5">
                        {liftingExtended
                          ? <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#b45309' }}><Icon name="ArrowUp" size={12} /> Extended</span>
                          : <span className="text-xs text-gray-400">No change</span>}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Payment By Date', value: formatDate(offer?.paymentByDate) },
                { label: 'Lifting By Date', value: formatDate(offer?.liftingByDate) },
              ]?.map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs font-medium mb-0.5" style={{ color: '#9ca3af' }}>{label}</p>
                  <p className="text-sm font-semibold" style={{ color: '#1f2937' }}>{value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Photos Section */}
          <div>
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1f2937' }}>
              <Icon name="Camera" size={16} color="#f59e0b" />
              PP Bag Photos
              <span className="text-xs font-normal px-2 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#92400e' }}>
                {offer?.photoUrls?.length || 0} photo{offer?.photoUrls?.length !== 1 ? 's' : ''}
              </span>
            </h3>
            {offer?.photoUrls?.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {offer?.photoUrls?.map((url, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setLightboxSrc(url)}
                    className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-amber-200 hover:border-amber-400 transition-all group"
                  >
                    <img
                      src={url}
                      alt={`PP Bag Photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                      <Icon name="ZoomIn" size={18} color="white" className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    {idx === 0 && (
                      <div className="absolute bottom-0 left-0 right-0 text-center text-[9px] font-bold py-0.5 text-white" style={{ background: 'rgba(217,119,6,0.85)' }}>
                        PP Bag
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: '#f9fafb', border: '1px dashed #d1d5db' }}>
                <Icon name="ImageOff" size={16} color="#9ca3af" />
                <p className="text-sm" style={{ color: '#9ca3af' }}>No photos uploaded</p>
              </div>
            )}
          </div>

          {/* Videos Section */}
          <div>
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1f2937' }}>
              <Icon name="Video" size={16} color="#f59e0b" />
              Videos
              <span className="text-xs font-normal px-2 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#92400e' }}>
                {offer?.videoUrls?.length || 0} video{offer?.videoUrls?.length !== 1 ? 's' : ''}
              </span>
            </h3>
            {offer?.videoUrls?.length > 0 ? (
              <div className="space-y-2">
                {offer?.videoUrls?.map((url, idx) => (
                  <div key={idx} className="rounded-xl overflow-hidden border" style={{ borderColor: '#f3e8d0' }}>
                    <video
                      src={url}
                      controls
                      className="w-full max-h-48 bg-black"
                      preload="metadata"
                    >
                      Your browser does not support the video tag.
                    </video>
                    <div className="flex items-center gap-2 px-3 py-2" style={{ background: '#fef9f0' }}>
                      <Icon name="Film" size={14} color="#d97706" />
                      <span className="text-xs font-medium" style={{ color: '#374151' }}>Video {idx + 1}</span>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto text-xs font-semibold flex items-center gap-1 hover:underline"
                        style={{ color: '#d97706' }}
                      >
                        <Icon name="ExternalLink" size={12} />
                        Open
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: '#f9fafb', border: '1px dashed #d1d5db' }}>
                <Icon name="VideoOff" size={16} color="#9ca3af" />
                <p className="text-sm" style={{ color: '#9ca3af' }}>No videos uploaded</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-5 border-t flex justify-end" style={{ borderColor: '#f3e8d0' }}>
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors hover:bg-amber-50" style={{ color: '#6b7280', border: '1px solid #e5e7eb' }}>
            Close
          </button>
        </div>
      </div>

      {/* Lightbox for photo zoom */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={() => setLightboxSrc(null)}
        >
          <img
            src={lightboxSrc}
            alt="Photo preview"
            className="max-w-full max-h-full rounded-xl object-contain"
            onClick={(e) => e?.stopPropagation()}
          />
          <button
            onClick={() => setLightboxSrc(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <Icon name="X" size={20} color="white" />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Approve Modal (Enhanced with Rate Editing) ────────────────────────────────
const ApproveModal = ({ offer, onConfirm, onCancel }) => {
  const isResume = offer?.isResumeRequest;
  const [finalRate, setFinalRate] = useState(String(offer?.offeredRate || ''));
  const [rateError, setRateError] = useState('');

  const handleRateChange = (e) => {
    const val = e?.target?.value;
    setFinalRate(val);
    if (val && isNaN(parseFloat(val))) {
      setRateError('Please enter a valid number');
    } else {
      setRateError('');
    }
  };

  const handleConfirm = () => {
    const parsed = parseFloat(finalRate);
    if (!finalRate || isNaN(parsed) || parsed <= 0) {
      setRateError('Please enter a valid rate');
      return;
    }
    onConfirm(parsed);
  };

  const rateChanged = parseFloat(finalRate) !== offer?.offeredRate;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" style={{ background: '#fffbf5', border: '1px solid #f3e8d0' }}>
        {/* Header */}
        <div className="px-6 py-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #f0fdf4, #fffbf5)', borderBottom: '1px solid #f3e8d0' }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#d1fae5' }}>
            <Icon name="CheckCircle" size={26} color="#059669" />
          </div>
          <div>
            <h3 className="text-base font-bold" style={{ color: '#1f2937' }}>
              {isResume ? 'Approve Resume Request' : 'Approve Offer'}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
              {offer?.factoryName} · {offer?.grade} · {offer?.quantity?.toLocaleString()} MT
            </p>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Rate Editing Section */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #d1fae5' }}>
            <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: '#f0fdf4', borderBottom: '1px solid #d1fae5' }}>
              <Icon name="IndianRupee" size={14} color="#059669" />
              <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#065f46' }}>Rate Review</span>
            </div>
            <div className="px-4 py-4 space-y-3" style={{ background: '#fffbf5' }}>
              {/* Offered Rate (read-only) */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium" style={{ color: '#9ca3af' }}>Offered Rate (by Buyer)</p>
                  <p className="text-lg font-extrabold mt-0.5" style={{ color: '#374151' }}>
                    ₹{offer?.offeredRate?.toLocaleString()}<span className="text-sm font-normal">/Ton</span>
                  </p>
                </div>
                <div className="px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ background: '#fef3c7', color: '#92400e' }}>
                  Buyer's Ask
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px" style={{ background: '#e5e7eb' }} />
                <Icon name="ArrowDown" size={14} color="#9ca3af" />
                <div className="flex-1 h-px" style={{ background: '#e5e7eb' }} />
              </div>

              {/* Final Rate (editable) */}
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#065f46' }}>
                  Final Selling Rate (₹/Ton) <span className="font-normal text-gray-400">— editable</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold" style={{ color: '#059669' }}>₹</span>
                  <input
                    type="number"
                    value={finalRate}
                    onChange={handleRateChange}
                    min="1"
                    step="1"
                    className="w-full pl-7 pr-16 py-2.5 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-green-300"
                    style={{
                      border: `1px solid ${rateError ? '#fca5a5' : rateChanged ? '#6ee7b7' : '#d1fae5'}`,
                      background: rateChanged ? '#f0fdf4' : '#fff',
                      color: '#1f2937',
                    }}
                    placeholder="Enter final rate"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium" style={{ color: '#9ca3af' }}>/Ton</span>
                </div>
                {rateError && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{rateError}</p>}
                {rateChanged && !rateError && (
                  <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#059669' }}>
                    <Icon name="Info" size={11} />
                    Rate adjusted from ₹{offer?.offeredRate?.toLocaleString()} → ₹{parseFloat(finalRate || 0)?.toLocaleString()}
                  </p>
                )}
              </div>

              {/* Note */}
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <Icon name="Info" size={13} color="#3b82f6" />
                <p className="text-xs" style={{ color: '#1e40af' }}>
                  This rate will be shown to buyers in the Marketplace.
                </p>
              </div>
            </div>
          </div>

          {/* Approval message */}
          <p className="text-sm text-center" style={{ color: '#6b7280' }}>
            {isResume
              ? 'This will reactivate the listing and make it visible in the Marketplace.' :'This will create a new listing with a "Verified Trader Offer" badge.'}
          </p>

          {/* Buttons */}
          <div className="flex gap-3 pt-1">
            <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl font-semibold text-sm border transition-colors hover:bg-gray-50" style={{ color: '#6b7280', borderColor: '#e5e7eb' }}>
              Cancel
            </button>
            <button onClick={handleConfirm} className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white transition-colors shadow-md" style={{ background: 'linear-gradient(135deg, #059669, #047857)' }}>
              Approve & Set Rate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Reject Modal ──────────────────────────────────────────────────────────────
const RejectModal = ({ offer, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="w-full max-w-md rounded-2xl shadow-2xl p-6" style={{ background: '#fffbf5', border: '1px solid #f3e8d0' }}>
        <div className="flex items-center justify-center mb-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#fee2e2' }}>
            <Icon name="XCircle" size={28} color="#dc2626" />
          </div>
        </div>
        <h3 className="text-lg font-bold text-center mb-2" style={{ color: '#1f2937' }}>Reject Offer</h3>
        <p className="text-sm text-center mb-4" style={{ color: '#6b7280' }}>
          Offer <span className="font-semibold">{offer?.readableOfferId}</span> will be rejected.
        </p>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1.5" style={{ color: '#374151' }}>
            Reason for Rejection <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e?.target?.value)}
            rows={3}
            placeholder="e.g. Incomplete documents, quality not meeting standards..."
            className="w-full px-3 py-2.5 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
            style={{ border: '1px solid #fca5a5', background: '#fff7f7', color: '#1f2937' }}
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl font-semibold text-sm border transition-colors hover:bg-gray-50" style={{ color: '#6b7280', borderColor: '#e5e7eb' }}>
            Cancel
          </button>
          <button onClick={() => onConfirm(reason)} className="flex-1 py-2.5 rounded-xl font-semibold text-sm text-white transition-colors" style={{ background: '#dc2626' }}>
            Reject
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Offer Card ────────────────────────────────────────────────────────────────
const OfferCard = ({ offer, onView, onApprove, onReject }) => {
  const isResume = offer?.isResumeRequest;
  const paymentExtended = isResume && isDateExtended(offer?.originalPaymentByDate, offer?.paymentByDate);
  const liftingExtended = isResume && isDateExtended(offer?.originalLiftingByDate, offer?.liftingByDate);

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#fffbf5', border: '1px solid #f3e8d0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      {/* Card Header */}
      <div className="flex items-start justify-between px-4 pt-4 pb-3" style={{ borderBottom: '1px solid #f3e8d0' }}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium" style={{ color: '#9ca3af' }}>Offer Ref:</span>
              <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(217,119,6,0.08)', color: '#b45309', border: '1px solid rgba(217,119,6,0.2)' }}>
                # {offer?.readableOfferId}
              </span>
            </div>
            {isResume ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: '#e0e7ff', color: '#3730a3' }}>
                <Icon name="RefreshCw" size={10} />
                Resume Request
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: '#fef3c7', color: '#92400e' }}>
                <Icon name="Sparkles" size={10} />
                New Offer
              </span>
            )}
          </div>
          <p className="text-base font-bold mt-1 truncate" style={{ color: '#1f2937' }}>{offer?.factoryName}</p>
          <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
            {offer?.sellerName} · {offer?.sellerMobile}
          </p>
        </div>
        <StatusBadge status={offer?.status} />
      </div>

      {/* Key Info Grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3">
        <div>
          <p className="text-xs font-medium" style={{ color: '#9ca3af' }}>Grade</p>
          <p className="text-sm font-bold mt-0.5" style={{ color: '#1f2937' }}>{offer?.grade}</p>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#9ca3af' }}>Season</p>
          <p className="text-sm font-bold mt-0.5" style={{ color: '#1f2937' }}>{offer?.season}</p>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#9ca3af' }}>Quantity</p>
          <p className="text-sm font-bold mt-0.5" style={{ color: '#1f2937' }}>{offer?.quantity?.toLocaleString()} MT</p>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#9ca3af' }}>Offered Rate</p>
          <p className="text-sm font-bold mt-0.5" style={{ color: '#059669' }}>₹{offer?.offeredRate?.toLocaleString()}/Ton</p>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#9ca3af' }}>Quality</p>
          <p className="text-sm font-semibold mt-0.5" style={{ color: '#374151' }}>{offer?.qualityRemarks}</p>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#9ca3af' }}>Payment By</p>
          <p className="text-sm font-semibold mt-0.5 flex items-center gap-1" style={{ color: paymentExtended ? '#b45309' : '#374151' }}>
            {formatDate(offer?.paymentByDate)}
            {paymentExtended && <span className="text-xs">↑</span>}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#9ca3af' }}>Lifting By</p>
          <p className="text-sm font-semibold mt-0.5 flex items-center gap-1" style={{ color: liftingExtended ? '#b45309' : '#374151' }}>
            {formatDate(offer?.liftingByDate)}
            {liftingExtended && <span className="text-xs">↑</span>}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2 px-4 pb-4 pt-1">
        <button
          onClick={() => onView(offer)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold transition-colors hover:bg-blue-50"
          style={{ color: '#1e4a72', border: '1px solid #bfdbfe', background: '#eff6ff' }}
        >
          <Icon name="Eye" size={14} />
          View Details
        </button>
        <button
          onClick={() => onApprove(offer)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors"
          style={{ background: '#059669' }}
        >
          <Icon name="CheckCircle" size={14} />
          Approve
        </button>
        <button
          onClick={() => onReject(offer)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors"
          style={{ background: '#dc2626' }}
        >
          <Icon name="XCircle" size={14} />
          Reject
        </button>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const SellOffersTab = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('new');
  const [viewOffer, setViewOffer] = useState(null);
  const [approveOffer, setApproveOffer] = useState(null);
  const [rejectOffer, setRejectOffer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('till_date');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  
  // New Offers: reserved + NOT a resume request
  // Resume Requests: reserved + IS a resume request
  const newOffers = offers?.filter((o) => o?.status === 'reserved' && !o?.isResumeRequest);
  const resumeRequests = offers?.filter((o) => o?.status === 'reserved' && o?.isResumeRequest);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        ?.from('quotas')
        ?.select('*')        
        ?.order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error fetching sell offers:', fetchError);
        setError('Unable to load sell offers. Please try again.');
        setOffers([]);
      } else {
        setOffers((data || [])?.map(mapQuotaToOffer));
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Something went wrong. Please refresh.');
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOffers();

    const channel = supabase
      ?.channel('qm_sell_offers_realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'quotas' }, () => {
        fetchOffers();
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, [fetchOffers]);

  const handleApproveConfirm = async (finalRate) => {
    if (!approveOffer) return;
    try {
      const updatePayload = { availability_status: 'available', rejection_reason: '' };
      if (finalRate && !isNaN(parseFloat(finalRate))) {
        updatePayload.price_per_ton = parseFloat(finalRate);
      }
      const { error: updateError } = await supabase
        ?.from('quotas')
        ?.update(updatePayload)
        ?.eq('id', approveOffer?.id);

      if (updateError) {
        console.error('Approve error:', updateError);
      } else {
        // Optimistic update — remove from pending list
        setOffers((prev) => prev?.filter((o) => o?.id !== approveOffer?.id));
        // Send email notification
        sendSellOfferEmail('approval', approveOffer?.sellerEmail, {
          id: approveOffer?.readableOfferId,
          sellerName: approveOffer?.sellerName,
          factoryName: approveOffer?.factoryName,
          grade: approveOffer?.grade,
          quantity: approveOffer?.quantity,
          offeredRate: approveOffer?.offeredRate,
          paymentByDate: approveOffer?.paymentByDate,
          liftingByDate: approveOffer?.liftingByDate,
        });
      }
    } catch (err) {
      console.error('Unexpected approve error:', err);
    }
    setApproveOffer(null);
  };

  const handleRejectConfirm = async (reason) => {
    if (!rejectOffer) return;
    try {
      const { error: updateError } = await supabase
        ?.from('quotas')
        ?.update({ availability_status: 'rejected', rejection_reason: reason || '' })
        ?.eq('id', rejectOffer?.id);

      if (updateError) {
        console.error('Reject error:', updateError);
      } else {
        // Optimistic update — remove from pending list
        setOffers((prev) => prev?.filter((o) => o?.id !== rejectOffer?.id));
        // Send email notification
        sendSellOfferEmail('rejection', rejectOffer?.sellerEmail, {
          id: rejectOffer?.readableOfferId,
          sellerName: rejectOffer?.sellerName,
          factoryName: rejectOffer?.factoryName,
          grade: rejectOffer?.grade,
          quantity: rejectOffer?.quantity,
          offeredRate: rejectOffer?.offeredRate,
          rejectionReason: reason || '',
        });
      }
    } catch (err) {
      console.error('Unexpected reject error:', err);
    }
    setRejectOffer(null);
  };

  const sections = [
    { id: 'new', label: 'New Offers', sublabel: 'Pending for Approval', count: newOffers?.length, icon: 'FileText', color: '#f59e0b' },
    { id: 'resume', label: 'Resume Requests', sublabel: 'Pending for Resume', count: resumeRequests?.length, icon: 'RefreshCw', color: '#6366f1' },
  ];

  const currentOffers = activeSection === 'new' ? newOffers : resumeRequests;
  const filteredOffers = useMemo(() => {
    const { from, to } = getDateRange(datePreset, customFrom, customTo);
    const q = searchTerm.trim().toLowerCase();
    return currentOffers.filter(offer => {
      if (q && !(
        offer.factoryName?.toLowerCase().includes(q) ||
        offer.grade?.toLowerCase().includes(q) ||
        offer.sellerName?.toLowerCase().includes(q) ||
        offer.sellerEmail?.toLowerCase().includes(q)
      )) return false;
      if (statusFilter !== 'all' && offer.status !== statusFilter) return false;
      if (from || to) {
        const d = new Date(offer.createdAt);
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      return true;
    });
  }, [currentOffers, searchTerm, statusFilter, datePreset, customFrom, customTo]);

  return (
    <div className="space-y-5">
      {/* Section Selector */}
      <div className="grid grid-cols-2 gap-3">
        {sections?.map((sec) => (
          <button
            key={sec?.id}
            onClick={() => setActiveSection(sec?.id)}
            className="flex items-center gap-3 p-3 rounded-2xl border-2 text-left transition-all"
            style={{
              background: activeSection === sec?.id ? '#fffbf5' : '#fff',
              borderColor: activeSection === sec?.id ? sec?.color : '#e5e7eb',
              boxShadow: activeSection === sec?.id ? `0 0 0 2px ${sec?.color}22` : 'none',
            }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${sec?.color}18` }}>
              <Icon name={sec?.icon} size={20} color={sec?.color} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-bold text-sm" style={{ color: '#1f2937' }}>{sec?.label}</p>
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold text-white" style={{ background: sec?.color }}>
                  {sec?.count}
                </span>
              </div>
              <p className="text-xs mt-0.5 truncate" style={{ color: '#9ca3af' }}>{sec?.sublabel}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Active Section Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-1 h-5 rounded-full flex-shrink-0" style={{ background: activeSection === 'new' ? '#f59e0b' : '#6366f1' }} />
        <h3 className="text-sm font-bold" style={{ color: '#1f2937' }}>
          {activeSection === 'new' ? 'New Offers — Pending for Approval' : 'Resume Requests — Pending for Resume'}
        </h3>
        {activeSection === 'resume' && (
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#e0e7ff', color: '#3730a3' }}>
            ↑ Extended dates highlighted
          </span>
        )}
        <button
          onClick={fetchOffers}
          className="ml-auto p-1.5 rounded-lg transition-colors hover:bg-orange-50"
          title="Refresh"
        >
          <Icon name="RefreshCw" size={14} color="#f97316" />
        </button>
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <DateRangeFilter
          preset={datePreset}
          customFrom={customFrom}
          customTo={customTo}
          onPresetChange={setDatePreset}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Icon name="Search" size={15} color="#9ca3af" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search factory, grade, seller name, email…"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none focus:ring-2 focus:ring-amber-300"
              style={{ borderColor: '#e5e7eb', color: '#1f2937' }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 rounded-xl border text-sm font-medium outline-none focus:ring-2 focus:ring-amber-300"
            style={{ borderColor: '#e5e7eb', color: '#374151', minWidth: '160px' }}
          >
            <option value="all">All Statuses</option>
            <option value="reserved">Pending</option>
            <option value="available">Approved</option>
            <option value="paused">Paused</option>
            <option value="withdrawn">Withdrawn</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      {/* Loading */}
      {loading && (
        <div className="py-16 flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-300 border-t-orange-500 rounded-full animate-spin" />
          <p className="text-sm" style={{ color: '#9ca3af' }}>Loading sell offers...</p>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="py-12 flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#fee2e2' }}>
            <Icon name="AlertCircle" size={24} color="#dc2626" />
          </div>
          <p className="font-semibold text-sm" style={{ color: '#374151' }}>{error}</p>
          <button onClick={fetchOffers} className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: '#f97316' }}>
            Try Again
          </button>
        </div>
      )}

      {/* Cards */}
      {!loading && !error && (
      filteredOffers.length === 0 ? (        
          <div className="text-center py-12">
            <Icon name="Inbox" size={40} color="#d1d5db" />
            <p className="mt-3 text-sm font-medium" style={{ color: '#9ca3af' }}>
              {activeSection === 'new' ? 'No new offers pending approval' : 'No resume requests pending'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOffers.map((offer) => (            
              <OfferCard
                key={offer?.id}
                offer={offer}
                onView={setViewOffer}
                onApprove={setApproveOffer}
                onReject={setRejectOffer}
              />
            ))}
          </div>
        )
      )}

      {/* Modals */}
      {viewOffer && <ViewDetailsModal offer={viewOffer} onClose={() => setViewOffer(null)} />}
      {approveOffer && <ApproveModal offer={approveOffer} onConfirm={handleApproveConfirm} onCancel={() => setApproveOffer(null)} />}
      {rejectOffer && <RejectModal offer={rejectOffer} onConfirm={handleRejectConfirm} onCancel={() => setRejectOffer(null)} />}
    </div>
  );
};

export default SellOffersTab;
